import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {FinishedResponseInteractionService} from 'shared/services';
import {Subkind} from 'shared/constants';
import type {FieldTypeId, SearchParams} from 'src/typings';
import type {GridCellEditRequest, RecordRow} from 'src/features/record-grid/lib/types';
import type {RecordGridService} from 'src/features/record-grid/services/record-grid.service';
import type {RecordGridDataService} from 'src/features/record-grid/services/record-grid.data-service';
import type {RecordBulkEditService} from 'src/features/record/record-bulk-edit.service';
import type RecordService from 'src/features/record/record.service';
import {convertFieldTypeIdToFieldId} from 'shared/utils/record-field-utils';
import {FOND_MONTHLY_ATTENDANCE} from 'src/features/erp-sutor/sutor-fonds';

export class MonthAttendanceService {
    public static serviceName = 'monthAttendanceService';

    /*@ngInject*/
    constructor(private recordBulkEditService: RecordBulkEditService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private toastMessageService: ToastMessageService,
                private recordService: RecordService,
                private recordGridService: RecordGridService,
                private recordGridDataService: RecordGridDataService) {
    }

    public async setState<PARAMS extends SearchParams = SearchParams>(params: PARAMS, workAttendanceStateId: string): Promise<void> {
        try {
            await this.recordBulkEditService.bulkEditRecords(params, {
                fieldEditRequests: [
                    {
                        id: convertFieldTypeIdToFieldId(FOND_MONTHLY_ATTENDANCE.stateFieldId, [0, 0]),
                        setFieldValueRequest: {
                            value: workAttendanceStateId
                        }
                    }
                ]
            });
            this.toastMessageService.showSuccess('Stav byl úspěšně změněn u vyfiltrovaných záznamů');
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    public async editMonthAttendance(record: RecordRow): Promise<RecordRow | null> {
        try {
            const updatedRec = await this.recordService.createRecordEditation({
                subkind: Subkind.SUBKIND_DOCUMENT
            }, record);

            return await this.recordGridService.loadSingleRow(updatedRec.id);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async editMonthAttendanceTextNote(record: RecordRow, textNote: string): Promise<RecordRow | null> {
        try {
            const editRequests: GridCellEditRequest[] = [
                {
                    record: record.id,
                    fieldId: convertFieldTypeIdToFieldId(FOND_MONTHLY_ATTENDANCE.textNoteFieldId, [0, 0]),
                    setFieldValueRequest: {value: textNote},
                    additionalInvalidatingRecords: []
                }
            ];

            await this.recordGridDataService.editCells({cellEditRequests: editRequests});
            this.toastMessageService.showSuccess('Poznámka byla úspěšně změněna');

            return await this.recordGridService.loadSingleRow(record.id);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async editPaymentState(record: RecordRow, fieldTypeId: FieldTypeId, state: string): Promise<RecordRow | null> {
        try {
            const editRequests: GridCellEditRequest[] = [
                {
                    record: record.id,
                    fieldId: convertFieldTypeIdToFieldId(fieldTypeId, [0, 0]),
                    setFieldValueRequest: {value: state},
                    additionalInvalidatingRecords: []
                }
            ];

            await this.recordGridDataService.editCells({cellEditRequests: editRequests});
            this.toastMessageService.showSuccess('Stav proplacení byl úspěšně změněn');

            return await this.recordGridService.loadSingleRow(record.id);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }
}